'use client';
import { AgentLineVideo } from '@/app/components/icons/AgentLineVideo';
import { BaseLinePlay } from '@/app/components/icons/BaseLinePlay';
import type { AgentMediaItem } from '@/app/types';
import { cn } from '@/app/utils';

const renderContent = ({ showPlayButton, data }: { data: AgentMediaItem; showPlayButton: boolean }) => {
  if (data.type === 'image' && data.url) {
    return <img src={data.url} alt={data.type} className="size-full object-cover" draggable={false} />;
  }
  if (data.type === 'video') {
    if (data.cover_url) {
      return (
        <div className="relative size-full">
          <img src={data.cover_url} className="size-full object-cover" draggable={false} alt={data.type} />
          {showPlayButton && (
            <div className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 flex size-2 items-center justify-center rounded-full bg-system-black64 backdrop-blur-[5px]">
              <BaseLinePlay className="size-1" />
            </div>
          )}
        </div>
      );
    }
    return (
      <div className="flex size-full items-center justify-center bg-[#6F6F6F]">
        <AgentLineVideo className="size-5 text-system-white/60" />
      </div>
    );
  }
};

export function CollapseMediaItemsDisplay({
  mediaFiles,
  baseTranslateX = 24,
  aspectRatio = 12 / 7,
  className,
  itemClassName,
  onPreview,
}: {
  mediaFiles: AgentMediaItem[];
  baseTranslateX?: number;
  aspectRatio?: number;
  className?: string;
  itemClassName?: string;
  onPreview?: (id: string) => void;
}) {
  // 最多显示 4 张卡片
  const displayItems = mediaFiles.slice(0, 4);
  const totalCards = displayItems.length;

  // // 计算整体偏移量：最后一张卡片相对于第一张的偏移量的一半
  const totalOffset = (totalCards - 1) * baseTranslateX;
  const centerOffset = totalOffset / 2;

  return (
    <div
      className={cn('relative flex w-full', className)}
      style={{
        aspectRatio: 336 / 141,
        transform: `translateX(-${centerOffset}px)`,
      }}
    >
      {displayItems.map((item, index) => {
        // 计算旋转角度：中间的卡片为0度，两边对称倾斜，每张相差2度
        const centerIndex = (totalCards - 1) / 2;
        const rotation = totalCards > 1 ? (index - centerIndex) * 2 : 0;

        // 堆叠效果，中间对齐
        const cardStyle = {
          left: '50%',
          transform: `translateX(calc(-50% + ${index * baseTranslateX}px)) rotate(${rotation}deg)`,
          zIndex: -index,
        };

        return (
          <div
            key={item.id}
            className={cn(
              'absolute h-full overflow-hidden shadow-[0_10px_20px_0_rgba(0,0,0,0.24)]',
              displayItems.length > 1 ? 'rounded-8' : 'rounded-t-8',
              itemClassName,
            )}
            style={{
              aspectRatio,
              ...cardStyle,
            }}
            onClick={() => onPreview?.(item.id)}
          >
            {/* 如果有多张卡片，第一张卡片不显示播放按钮 */}
            {renderContent({ showPlayButton: !(mediaFiles.length > 1 && index === 0), data: item })}
          </div>
        );
      })}
    </div>
  );
}
